import { Check, CreditCard, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useSubscription, PLANS, PlanTier } from "@/contexts/SubscriptionContext";
import { format } from "date-fns";

const planOrder: PlanTier[] = ["starter", "professional", "enterprise"];

const SubscriptionPage = () => {
  const { subscription, upgradePlan, cancelSubscription, renewSubscription } = useSubscription();
  const currentPlan = PLANS[subscription.plan];

  const trialProgress = subscription.status === "trial"
    ? Math.round(((120 - subscription.daysRemaining) / 120) * 100)
    : 100;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Subscription</h1>
        <p className="text-sm text-muted-foreground">Manage your plan and billing</p>
      </div>

      {/* Current Status Card */}
      <div className="p-5 rounded-lg bg-card border border-border shadow-card space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            {subscription.status === "trial" ? (
              <Clock className="h-5 w-5 text-accent" />
            ) : subscription.status === "cancelled" ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : (
              <CreditCard className="h-5 w-5 text-accent" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">
                {subscription.status === "trial"
                  ? `Free Trial — ${subscription.daysRemaining} days remaining`
                  : subscription.status === "active"
                  ? `${currentPlan.name} Plan — Active`
                  : subscription.status === "cancelled"
                  ? `${currentPlan.name} Plan — Cancelled`
                  : `${currentPlan.name} Plan — Expired`}
              </p>
              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                subscription.status === "trial" ? "bg-accent/10 text-accent" :
                subscription.status === "active" ? "bg-emerald-500/10 text-emerald-600" :
                "bg-destructive/10 text-destructive"
              }`}>
                {subscription.status}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {subscription.status === "trial"
                ? `Trial ends on ${format(subscription.trialEndDate, "dd MMM yyyy")}`
                : `Current period ends ${format(subscription.currentPeriodEnd, "dd MMM yyyy")}`}
            </p>
          </div>
          {subscription.status === "cancelled" && (
            <Button size="sm" onClick={renewSubscription} className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Renew
            </Button>
          )}
        </div>

        {subscription.status === "trial" && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Trial progress</span>
              <span>{120 - subscription.daysRemaining} of 120 days used</span>
            </div>
            <Progress value={trialProgress} className="h-2" />
          </div>
        )}
      </div>

      {/* Renewal Reminder */}
      {subscription.status === "trial" && subscription.daysRemaining <= 30 && (
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Trial ending soon</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your free trial expires in {subscription.daysRemaining} days. Choose a plan below to continue uninterrupted access to AccouPro.
            </p>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {planOrder.map((tier) => {
          const plan = PLANS[tier];
          const isCurrent = subscription.plan === tier && subscription.status !== "trial";
          const isTrialPlan = subscription.plan === tier && subscription.status === "trial";

          return (
            <div
              key={tier}
              className={`p-6 rounded-lg border shadow-card transition-all ${
                isCurrent || isTrialPlan
                  ? "border-accent bg-accent/5"
                  : "border-border bg-card hover:border-accent/30"
              }`}
            >
              {isTrialPlan && (
                <span className="text-xs font-semibold text-accent mb-2 block">Trial Plan</span>
              )}
              {isCurrent && (
                <span className="text-xs font-semibold text-accent mb-2 block">Current Plan</span>
              )}
              <h3 className="text-lg font-bold font-display text-foreground">{plan.name}</h3>
              <p className="text-2xl font-bold font-display text-foreground mt-2">
                {plan.priceLabel}
                <span className="text-sm text-muted-foreground font-normal">/month</span>
              </p>

              <div className="text-xs text-muted-foreground mt-1">
                {plan.maxCompanies ? `${plan.maxCompanies} ${plan.maxCompanies === 1 ? "company" : "companies"}` : "Unlimited companies"} ·{" "}
                {plan.maxUsers ? `${plan.maxUsers} users` : "Unlimited users"}
              </div>

              <Button
                className="w-full mt-4"
                variant={isCurrent ? "outline" : "default"}
                disabled={isCurrent}
                onClick={() => !isCurrent && upgradePlan(tier)}
              >
                {isCurrent ? "Current Plan" : isTrialPlan ? "Activate Now" : "Upgrade"}
              </Button>

              {isCurrent && subscription.status === "active" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-xs text-muted-foreground hover:text-destructive"
                  onClick={cancelSubscription}
                >
                  Cancel Subscription
                </Button>
              )}

              <ul className="mt-4 space-y-2">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="h-3.5 w-3.5 text-accent flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionPage;
